import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import MangaCard from '@/components/feature/MangaCard';
import Footer from '@/components/feature/Footer';
import { categories, mockMangaList } from '@/mocks/manga';
import { useState, useMemo } from 'react';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'update' | 'views' | 'rating'>('update');

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || '全部';

  const filteredList = useMemo(() => {
    const list = slug
      ? mockMangaList.filter((m) => m.categorySlug === slug)
      : mockMangaList;

    if (sortBy === 'views') return [...list].sort((a, b) => b.views - a.views);
    if (sortBy === 'rating') return [...list].sort((a, b) => b.rating - a.rating);
    return [...list].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
  }, [slug, sortBy]);

  const sortOptions = [
    { value: 'update' as const, label: '最新更新' },
    { value: 'views' as const, label: '人气最高' },
    { value: 'rating' as const, label: '评分最高' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100/70 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/rexue' },
                { label: categoryName },
              ]}
            />
            <div className="pb-6">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                {categoryName}漫画
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredList.length} 部{categoryName}漫画免费阅读
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <Link
              to="/category/rexue"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                !slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.value
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {filteredList.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
              {filteredList.map((manga) => (
                <MangaCard key={manga.id} {...manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，去看看其他分类吧</p>
              <Link
                to="/category/rexue"
                className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors"
              >
                浏览热门分类
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}